import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        return data; // { step: "totp_setup" | "totp_verify", ... }
    };

    const verifyTotp = async (pending_token, code) => {
        const { data } = await api.post("/auth/totp/verify", { pending_token, code });
        setToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const setupTotpConfirm = async (pending_token, code) => {
        const { data } = await api.post("/auth/totp/setup-confirm", { pending_token, code });
        setToken(data.access_token);
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, verifyTotp, setupTotpConfirm, logout, refresh, formatApiError }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
